/** In-memory results owned by a single mounted browse page. Never persisted. */
export class PageQueryCache<T> {
    private entries = new Map<string, { value: T; expiresAt: number }>();
    private pending?: { key: string; controller: AbortController; promise: Promise<T> };

    constructor(private readonly ttlMs = 60_000, private readonly maxEntries = 20) {}

    get(key: string): T | undefined {
        const entry = this.entries.get(key);
        if (!entry) return undefined;
        if (entry.expiresAt <= Date.now()) {
            this.entries.delete(key);
            return undefined;
        }
        this.entries.delete(key);
        this.entries.set(key, entry);
        return entry.value;
    }

    load(key: string, fetch: (signal: AbortSignal) => Promise<T>): Promise<T> {
        if (this.pending?.key === key) return this.pending.promise;
        this.cancel();
        const cached = this.get(key);
        if (cached !== undefined) return Promise.resolve(cached);

        const controller = new AbortController();
        const promise = Promise.resolve().then(() => {
            if (controller.signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
            return fetch(controller.signal);
        }).then(value => {
            // Also guards transports that complete despite cancellation.
            if (controller.signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
            this.entries.set(key, { value, expiresAt: Date.now() + this.ttlMs });
            while (this.entries.size > this.maxEntries) {
                this.entries.delete(this.entries.keys().next().value!);
            }
            return value;
        }).finally(() => {
            if (this.pending?.controller === controller) this.pending = undefined;
        });
        this.pending = { key, controller, promise };
        return promise;
    }

    cancel() {
        this.pending?.controller.abort();
        this.pending = undefined;
    }

    clear() {
        this.cancel();
        this.entries.clear();
    }
}

export const isBrowseRequestCancelled = (error: unknown) => {
    const cancellation = error as { name?: string; code?: string } | null;
    return cancellation?.name === 'AbortError' || cancellation?.code === 'ERR_CANCELED';
};
